import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"
import { CameraDevice, Html5Qrcode, Html5QrcodeResult, Html5QrcodeSupportedFormats } from "html5-qrcode"
import { Html5QrcodeError } from "html5-qrcode/core"
import { Html5QrcodeCameraScanConfig } from "html5-qrcode/src/html5-qrcode"
import styles from "./QRScan.module.scss"

const qrcodeRegionId = "html5qr-code-full-region"

import * as Sentry from "@sentry/react"
import { useTranslation } from "react-i18next"
import { detectOS } from "../../utils/detectOS"
import { setCookie } from "../../utils/cookies"
import { MAX_AGE_TOKEN_180_DAYS_IN_SECONDS, SELECTED_QR_CAMERA } from "../../utils/constants"
import moment from "moment/moment"

interface Props {
  config?: Html5QrcodeCameraScanConfig
  qrCodeSuccessCallback?: (decodedText: string) => void
  callbackError?: (err?: any) => void
  nowActiveCamera: number
  setQrLoading: Dispatch<SetStateAction<boolean>>
  qrIsError: boolean
  setQrIsError: Dispatch<SetStateAction<boolean>>
  setIsManualActive: Dispatch<SetStateAction<boolean>>
  setCameraChanging?: Dispatch<SetStateAction<boolean>>
}

const formatsToSupport = [Html5QrcodeSupportedFormats.QR_CODE]

const configScan = {
  fps: 8,
  aspectRatio: 1.333334,
  facingMode: { exact: "environment" },
  focusMode: "continuous",
  formatsToSupport: formatsToSupport,
  qrbox: { width: 260, height: 260 },
}

const Html5QrcodePlugin = ({
  config = { fps: 15 },
  setQrLoading,
  nowActiveCamera,
  qrCodeSuccessCallback,
  callbackError,
  qrIsError,
  setQrIsError,
  setIsManualActive,
  setCameraChanging,
}: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const [customLoader, setCustomLoader] = useState(false)
  const [devicesArr, setDevicesArr] = useState<CameraDevice[]>([])
  // const [testError, setTestError] = useState('')

  const qrCodeErrorCallback = (decodedText: string, decodedResult: Html5QrcodeError) => {
    const ignorableErrors = [
      "No MultiFormat Readers were able to detect the code",
      "No barcode or QR code detected",
      "QR code parse error, error = No barcode or QR code detected.",
      "QR code parse error, error = NotFoundException: No MultiFormat Readers were able to detect the code.",
    ]

    const err1 = " No MultiFormat Readers were able to detect the code",
      err2 = "No barcode or QR code detected",
      err3 = "Failed to execute 'getImageData' on 'CanvasRenderingContext2D': The source width is 0",
      err4 = "Failed to execute 'detect' on 'BarcodeDetector':",
      err5 = "QR code parse error, error = No largest barcode found"

    if (
      decodedResult &&
      decodedResult.errorMessage &&
      !decodedResult.errorMessage.includes(err1) &&
      !decodedResult.errorMessage.includes(err2) &&
      !decodedResult.errorMessage.includes(err3) &&
      !decodedResult.errorMessage.includes(err4) &&
      !decodedResult.errorMessage.includes(err5)
    ) {
      if (decodedText === "{}" || decodedText === "") return
      Sentry.captureException(Error(`Ошибка считывания QR. decodedText: ${decodedText};`))
      // setTestError(`Error 1: ${String(decodedResult.errorMessage)}`)
      // console.log('decodedText ', decodedText)
      // if (callbackError) callbackError()
    }
  }

  const nowOs = detectOS()
  const html5QrCode = useRef<any>()

  const qrCodeSuccessCallbackPlugin = (decodedText: string, result: Html5QrcodeResult) => {
    if (html5QrCode.current?.isScanning) {
      html5QrCode.current.stop().then(() => {
        html5QrCode.current.clear()
      })
    }
    // navigator?.vibrate([50])
    if (qrCodeSuccessCallback) qrCodeSuccessCallback(decodedText)
  }

  useEffect(() => {
    if (html5QrCode.current) {
      if (html5QrCode.current?.isScanning) {
        html5QrCode.current.stop().then(() => {
          html5QrCode.current.clear()
          setCookie(SELECTED_QR_CAMERA, nowActiveCamera % devicesArr.length, {
            maxAge: MAX_AGE_TOKEN_180_DAYS_IN_SECONDS,
            sameSite: "lax",
            path: process.env.NEXT_PUBLIC_ASSET_PREFIX || "/",
            expires: new Date(moment().add(180, "d").toDate()),
          })
          html5QrCode.current
            .start(
              devicesArr[nowActiveCamera % devicesArr.length]?.id ??
                devicesArr[1]?.id ??
                devicesArr[0]?.id ??
                qrcodeRegionId,
              // qrcodeRegionId,
              configScan,
              qrCodeSuccessCallbackPlugin,
              qrCodeErrorCallback,
            )
            .then(() => {
              setQrLoading(true)
              if (setCameraChanging) setCameraChanging(false)
            })
            .catch((err: any) => {
              setQrLoading(true)
              setQrIsError(true)
              const errorTxt = JSON.stringify(err)
              if (errorTxt === "{}" || errorTxt === "") return
              Sentry.captureException(Error(`Ошибка старта считывания QR. ${JSON.stringify(err)}`))
            })
        })
      }
    }
  }, [nowActiveCamera])

  useEffect(() => {
    let isMounted = true
    // const verbose = props.verbose === true
    // Success callback is required.
    if (!qrCodeSuccessCallback) {
      throw "qrCodeSuccessCallback is required callback."
    }

    html5QrCode.current = new Html5Qrcode(qrcodeRegionId)
    Html5Qrcode.getCameras()
      .then((devices) => {
        setDevicesArr(devices)
        const currentDevices = devices.length > 0 ? devices[nowActiveCamera % devices.length]?.id : devices[0]?.id

        html5QrCode.current
          .start(
            currentDevices ?? devices[1]?.id ?? devices[0]?.id ?? qrcodeRegionId,
            // qrcodeRegionId,
            configScan,
            qrCodeSuccessCallbackPlugin,
            qrCodeErrorCallback,
          )
          .then(() => {
            setQrLoading(true)
          })
          .catch((err: any) => {
            setQrLoading(true)
            setQrIsError(true)
            const errorTxt = JSON.stringify(err)
            if (errorTxt === "{}" || errorTxt === "") return
            Sentry.captureException(Error(`Ошибка старта считывания QR. ${JSON.stringify(err)}`))
          })
      })
      .catch((err: any) => {
        setQrLoading(true)
        setQrIsError(true)
        const errorTxt = JSON.stringify(err)
        if (errorTxt === "{}" || errorTxt === "") return
        // Проверка, что бы не шли ошибки в сентри, если компонент просто размонтировался
        if (isMounted) {
          Sentry.captureException(Error(`Ошибка старта считывания QR. ${JSON.stringify(err)}`))
        }
      })

    return () => {
      isMounted = false
      if (html5QrCode.current?.isScanning) {
        html5QrCode.current.stop().then(() => {
          html5QrCode.current.clear()
        })
      }
    }
  }, [])

  const onLoadQrFile = (ev: any) => {
    setCustomLoader(true)
    html5QrCode.current
      .scanFile(ev.target.files[0])
      .then((resp: any) => {
        setQrLoading(true)
        if (qrCodeSuccessCallback) qrCodeSuccessCallback(resp)
      })
      .catch((err: any) => {
        setQrLoading(true)
        if (callbackError) callbackError(t("qrFileError"))
        // Sentry.captureException(Error(`Ошибка старта считывания QR из картинки. ${JSON.stringify(err)}`))
      })
  }

  return (
    <div className={styles.scannerhtml5}>
      <div id={qrcodeRegionId} className={customLoader ? styles.scannerhtml5__load : ""} />

      {/*{testError !== '' && (
        <div className={styles.testError}>
          {testError}
        </div>
      )}*/}
      {customLoader ? (
        <div className={styles.loader}>
          <img src="/img/loading.svg" alt="" />
        </div>
      ) : (
        qrIsError && (
          <div className={styles.loadFile}>
            <div className={styles.loadFile__box}>
              <div className={styles.loadFile__label}>
                {t("qrCameraError2")}
                <span className={styles.manual} onClick={() => setIsManualActive(true)}>
                  {t("enterManual")}
                </span>
              </div>
              <label className={styles.loadFile__btn}>
                {nowOs === "iOS" ? (
                  <input type="file" accept="image/*" capture onChange={onLoadQrFile} />
                ) : (
                  <input type="file" accept="image/*;capture=camera" onChange={onLoadQrFile} />
                )}
                <svg
                  className={styles.loadFile__icon}
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M8.99967 5.66659H5.66634V8.99992C5.66634 9.36659 5.36634 9.66659 4.99967 9.66659C4.63301 9.66659 4.33301 9.36659 4.33301 8.99992V5.66659H0.999674C0.633008 5.66659 0.333008 5.36659 0.333008 4.99992C0.333008 4.63325 0.633008 4.33325 0.999674 4.33325H4.33301V0.999919C4.33301 0.633252 4.63301 0.333252 4.99967 0.333252C5.36634 0.333252 5.66634 0.633252 5.66634 0.999919V4.33325H8.99967C9.36634 4.33325 9.66634 4.63325 9.66634 4.99992C9.66634 5.36659 9.36634 5.66659 8.99967 5.66659Z"></path>
                </svg>
                {t("uploadFile")}
              </label>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default Html5QrcodePlugin
